<script lang="ts" setup>
import type { DynamicAuctionItemList } from '@autobid/strapi-integration/rest/components'
import type { Counters } from '@autobid/ui/types/components/CarsList'
import { useCarsListFilters } from '@autobid/ui/composables/car/data/useCarsListFilters'
import { useIsMobile } from '@autobid/ui/composables/useIsMobile'
import { COUNTERS_EMPTY_STATE } from '@autobid/ui/constants/COUNTERS_EMPTY_STATE'
import { computed, inject } from 'vue'

interface Props {
  counters?: Counters
  showDeliverer?: boolean
}

const props = defineProps<Props>()

const auctionStrapiData = inject<DynamicAuctionItemList>('auction-strapi-data')
const isMobile = useIsMobile()
const { filtersQty, filtersTabActive } = useCarsListFilters()
const countersWithAddedDefaultState = computed(() => {
  return {
    ...COUNTERS_EMPTY_STATE,
    ...props.counters,
  }
})
</script>

<template>
  <div
    class="car-list-filters fixed inset-0 z-[60] overflow-y-auto bg-white px-3 pb-6 duration-300 lg:visible lg:static lg:z-10 lg:w-[270px] lg:min-w-[270px] lg:overflow-visible lg:py-0 lg:opacity-100"
    :class="{
      'invisible opacity-0': !filtersTabActive,
    }"
  >
    <div
      class="sticky top-0 z-50 -mx-3 mb-2 flex justify-between bg-white/30 px-3 pb-2 pt-6 backdrop-blur-sm lg:hidden"
    >
      <DynamicAuctionItemListSorting class="lg:hidden" />

      <button @click="filtersTabActive = false">
        <Icon mode="svg" class="h-8 w-8 text-primary" name="uil:times" />
      </button>
    </div>
    <client-only>
      <LazyDynamicAuctionItemListFiltersActiveFiltersMobile
        v-if="isMobile && filtersQty"
      />
    </client-only>

    <common-filters-dropdown v-if="auctionStrapiData?.type === 'carList'" :hide-title="true">
      <template #content>
        <DynamicAuctionItemListFiltersStatuses :counters="countersWithAddedDefaultState" />
      </template>
    </common-filters-dropdown>

    <common-filters-dropdown card-key="manufacturers">
      <template #title>
        {{ $t('cars-list.filters.manufacturer') }}
      </template>

      <template #content>
        <DynamicAuctionItemListFiltersManufacturer />
      </template>
    </common-filters-dropdown>

    <DynamicAuctionItemListFiltersCategoriesAll v-if="counters" :counters="counters" />

    <DynamicAuctionItemListFiltersOtherFeatures v-if="counters" :counters="counters" />

    <DynamicAuctionItemListFiltersSuppliers
      v-if="auctionStrapiData?.type === 'carList' && showDeliverer"
      :counters="countersWithAddedDefaultState"
    />

    <DynamicAuctionItemListFiltersFuelType v-if="counters" :counters="counters" />

    <LazyDynamicAuctionItemListFiltersBasicData v-if="auctionStrapiData?.type === 'carSearch'" />
  </div>

  <LazyDynamicAuctionItemListFiltersActiveFiltersDesktop
    v-if="!isMobile && filtersQty"
  />
</template>

<style lang="scss">
.car-list-filters {
  p {
    font-weight: 600;
  }
  .checkbox {
    width: 100%;
  }
}
</style>
